import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import type { DailyLog, WeeklyAssessment, UserProfile, Medication } from '../types';
import { SEVERITY_LABELS } from '../types';
import { calculateTNSS, calculateTOSS, calculateTotalSymptomScore } from './helpers';

function formatDecimalIT(n: number | null): string {
  if (n === null || n === undefined) return '';
  return n.toString().replace('.', ',');
}

function formatDateIT(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return format(d, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

function getDayOfWeek(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'EEEE', { locale: it });
  } catch {
    return '';
  }
}

export function exportToXLSX(
  logs: DailyLog[],
  assessments: WeeklyAssessment[],
  profile: UserProfile | null,
  medications: Medication[]
) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Daily logs
  const dailyHeaders = [
    'Data', 'Giorno', 'VAS (0-10)', 'Classificazione ARIA',
    // Nasal
    'Starnuti (0-3)', 'Naso che cola (0-3)', 'Congestione (0-3)', 'Prurito nasale (0-3)', 'TNSS Totale',
    // Ocular
    'Occhi prurito (0-3)', 'Occhi lacrimanti (0-3)', 'Occhi rossi (0-3)', 'TOSS Totale',
    // Respiratory
    'Tosse (0-3)', 'Respiro sibilante (0-3)', 'Dispnea (0-3)',
    // Skin
    'Orticaria (0-3)', 'Prurito/Eczema (0-3)',
    // Systemic
    'Stanchezza (0-3)', 'Mal di testa (0-3)',
    'Punteggio Totale Sintomi',
    // ARIA
    'Sonno alterato', 'Attivita\' compromesse', 'Lavoro compromesso', 'Fastidioso',
    // Context
    'Farmaci', 'Ore sonno', 'Qualita\' sonno (0-10)', 'Stress (0-10)',
    'Ore all\'aperto', 'Ambiente', 'AC accesa', 'Modalita\' AC',
    'Finestre aperte (mare)', 'Durata finestre mare (min)',
    'Finestre aperte (strada)', 'Durata finestre strada (min)',
    'Auto', 'Aria auto',
    'Esercizio all\'aperto', 'Alcol', 'Contatto animali', 'Pulita sabbietta', 'Graffiato da Misù', 'Cartone',
    'Cibi cross-reattivi', 'Note',
    // Weather
    'Temperatura (°C)', 'Umidita\' (%)', 'Vento (km/h)', 'Pioggia (mm)',
    'Pressione (hPa)', 'PM2.5', 'UV Index',
    'Polline Graminacee', 'Polline Betulla', 'Polline Olivo',
    'Polline Artemisia', 'Polline Ambrosia', 'Polline Ontano',
  ];

  const dailyData = logs.map(log => [
    formatDateIT(log.date),
    getDayOfWeek(log.date),
    log.vasScore,
    [log.affectedSleep, log.impairedActivities, log.impairedWork, log.troublesome].some(Boolean) ? 'Moderato-Severo' : 'Lieve',
    log.sneezing, log.runnyNose, log.congestion, log.nasalItch, calculateTNSS(log),
    log.itchyEyes, log.wateryEyes, log.redEyes, calculateTOSS(log),
    log.cough, log.wheezing, log.shortnessOfBreath,
    log.urticaria, log.itchingEczema,
    log.fatigue, log.headache,
    calculateTotalSymptomScore(log),
    log.affectedSleep ? 'Si\'' : 'No',
    log.impairedActivities ? 'Si\'' : 'No',
    log.impairedWork ? 'Si\'' : 'No',
    log.troublesome ? 'Si\'' : 'No',
    log.medicationsTaken.join(', '),
    formatDecimalIT(log.sleepHours),
    formatDecimalIT(log.sleepQuality),
    formatDecimalIT(log.stressLevel),
    formatDecimalIT(log.outdoorHours),
    log.outdoorEnvironment.join(', '),
    log.acOn ? 'Si\'' : 'No',
    log.acMode || '',
    log.windowsOpenSea ? 'Si\'' : 'No',
    formatDecimalIT(log.windowsSeaDuration),
    log.windowsOpenStreet ? 'Si\'' : 'No',
    formatDecimalIT(log.windowsStreetDuration),
    log.carTravel ? 'Si\'' : 'No',
    log.carAirMode || '',
    log.exerciseOutdoor ? 'Si\'' : 'No',
    log.alcoholType || '',
    log.petContact ? 'Si\'' : 'No',
    log.cleanedLitterBox ? 'Si\'' : 'No',
    log.scratchedByMisu ? 'Si\'' : 'No',
    log.handledCardboard ? 'Si\'' : 'No',
    log.crossReactiveFoods.join(', '),
    log.notes,
    formatDecimalIT(log.temperature),
    formatDecimalIT(log.humidity),
    formatDecimalIT(log.windSpeed),
    formatDecimalIT(log.rain),
    formatDecimalIT(log.pressure),
    formatDecimalIT(log.pm25),
    formatDecimalIT(log.uvIndex),
    formatDecimalIT(log.pollenGrass),
    formatDecimalIT(log.pollenBirch),
    formatDecimalIT(log.pollenOlive),
    formatDecimalIT(log.pollenMugwort),
    formatDecimalIT(log.pollenRagweed),
    formatDecimalIT(log.pollenAlder),
  ]);

  const wsDaily = XLSX.utils.aoa_to_sheet([dailyHeaders, ...dailyData]);
  wsDaily['!cols'] = dailyHeaders.map(() => ({ wch: 18 }));
  XLSX.utils.book_append_sheet(wb, wsDaily, 'Registrazioni Giornaliere');

  // Sheet 2: Weekly Assessments
  if (assessments.length > 0) {
    const weeklyHeaders = ['Settimana', 'RCAT Totale (6-30)', 'RCAT Controllato'];
    const weeklyData = assessments.map(a => [
      formatDateIT(a.weekStartDate),
      a.rcatTotal,
      a.rcatTotal > 21 ? 'Si\'' : 'No',
    ]);
    const wsWeekly = XLSX.utils.aoa_to_sheet([weeklyHeaders, ...weeklyData]);
    XLSX.utils.book_append_sheet(wb, wsWeekly, 'Valutazioni Settimanali');
  }

  // Sheet 3: Metadata
  const metaData = [
    ['AllergoAway - Export Dati'],
    ['Data esportazione', formatDateIT(new Date().toISOString())],
    [''],
    ['Profilo Utente'],
    ['Nome', profile?.name || ''],
    ['Regione', profile?.region || ''],
    ['Allergie note', profile?.knownAllergies.join(', ') || ''],
    [''],
    ['Farmaci'],
    ...medications.map(m => [m.name, m.type, m.timing === 'prophylactic' ? 'Profilattico' : 'Al bisogno']),
    [''],
    ['Legenda Scale di Valutazione'],
    ['VAS (Visual Analogue Scale)', '0-10, dove 0 = nessun sintomo, 10 = peggior sintomo possibile'],
    ['ARIA < 5', 'Rinite lieve (VPN 93,5%)'],
    ['ARIA > 6', 'Rinite moderata-severa (VPP 73,6%)'],
    [''],
    ['Scala Sintomi (0-3)'],
    ...Object.entries(SEVERITY_LABELS).map(([k, v]) => [k, v]),
    [''],
    ['TNSS (Total Nasal Symptom Score)', 'Range 0-12'],
    ['TOSS (Total Ocular Symptom Score)', 'Range 0-9'],
    ['RCAT (Rhinitis Control Assessment Test)', 'Range 6-30, <=21 = non controllato'],
    [''],
    ['Fonti dati ambientali'],
    ['Meteo', 'Open-Meteo API'],
    ['Pollini', 'Copernicus/CAMS via Open-Meteo Air Quality API'],
    ['Calendario pollinico', 'POLLnet/ISPRA (62 stazioni in 18 regioni italiane)'],
  ];
  const wsMeta = XLSX.utils.aoa_to_sheet(metaData);
  wsMeta['!cols'] = [{ wch: 40 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, wsMeta, 'Metadati');

  // Generate and save
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const filename = `AllergoAway_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  saveAs(blob, filename);
}

export function exportToCSV(logs: DailyLog[]) {
  const headers = [
    'Data', 'VAS', 'Starnuti', 'Naso_cola', 'Congestione', 'Prurito_nasale',
    'Occhi_prurito', 'Occhi_lacrimanti', 'Occhi_rossi',
    'Tosse', 'Respiro_sibilante', 'Dispnea',
    'Orticaria', 'Prurito_eczema', 'Stanchezza', 'Mal_testa',
    'TNSS', 'TOSS', 'Totale', 'Farmaci', 'Ore_sonno', 'Note'
  ];

  const rows = logs.map(log => [
    formatDateIT(log.date),
    log.vasScore,
    log.sneezing, log.runnyNose, log.congestion, log.nasalItch,
    log.itchyEyes, log.wateryEyes, log.redEyes,
    log.cough, log.wheezing, log.shortnessOfBreath,
    log.urticaria, log.itchingEczema, log.fatigue, log.headache,
    calculateTNSS(log), calculateTOSS(log), calculateTotalSymptomScore(log),
    `"${log.medicationsTaken.join(', ')}"`,
    formatDecimalIT(log.sleepHours),
    `"${log.notes}"`
  ]);

  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const filename = `AllergoAway_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  saveAs(blob, filename);
}

export function exportJSON(data: object) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const filename = `AllergoAway_backup_${format(new Date(), 'yyyy-MM-dd')}.json`;
  saveAs(blob, filename);
}
